import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../components/AuthGate';
import { setChimeMuted } from '../utils/playChime';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const CACHE_KEY = 'soundMuted';

function readCache() {
  try {
    return localStorage.getItem(CACHE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function writeCache(value) {
  try {
    localStorage.setItem(CACHE_KEY, value ? '1' : '0');
  } catch (_) {}
}

// Server value (user.sound_muted, served on /auth/me) wins; localStorage is only the
// fallback when the API didn't send one (old build, AUTH_DISABLED dev user).
export default function useSoundPref() {
  const { user } = useAuth();
  const serverValue = typeof user?.sound_muted === 'boolean' ? user.sound_muted : null;
  const [muted, setMuted] = useState(serverValue ?? readCache());
  const latest = useRef(muted);
  latest.current = muted;

  useEffect(() => {
    if (serverValue !== null) {
      setMuted(serverValue);
      writeCache(serverValue);
    }
  }, [serverValue]);

  useEffect(() => {
    setChimeMuted(muted);
  }, [muted]);

  const toggle = useCallback(() => {
    const next = !muted;
    setMuted(next);
    writeCache(next);
    fetch(`${API_BASE_URL}/me/preferences`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sound_muted: next }),
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
      })
      .catch(() => {
        // Only roll back if no later toggle has superseded this one
        if (latest.current !== next) return;
        setMuted(!next);
        writeCache(!next);
      });
  }, [muted]);

  return { muted, toggle };
}
